import type { Compendium } from "@lancer-vtt/compendium";
import { COMPCON_ENGAGED_STATUS, hasStatus, type Combatant, type EncounterState, type HeldAction, type ProjectedEncounterState, type TurnBudget, heldAction, REPAIR_CAP_RESOURCE } from "@lancer-vtt/domain";
import { type ActionDescriptor, type MechActionValueKind, type LoadedOverlay, mechActionValueLabel, npcActionValueLabel, enumerateActions, cannotMakeAttacks, gatherInvadeOptions, GRAPPLED_STATUS, grapplePartner, hasCompConSystem, liveBackswingReservations } from "@lancer-vtt/rules";
import type { RequestJockeyMenu } from "@lancer-vtt/shared";
import type { UseEncounter } from "../useEncounter";
import { buildActivateIntent, buildStabilizeIntent, buildOverchargeIntent, buildEndAttackIntent, buildHideIntent, buildDisengageIntent, buildShutDownIntent, buildBootUpIntent, buildSelfDestructIntent, buildEngageCompConIntent, buildTakeBackCompConIntent, buildDetectMineIntent, buildDisarmMineIntent, buildSearchIntent, buildGrappleBreakIntent, buildGrappleContestIntent, buildGrappleReleaseIntent, buildReloadIntent, buildMountIntent, buildJockeyIntent, buildRideIntent, buildJockeyMenuIntent, buildShakeOffIntent, type StabilizePrimary, type StabilizeSecondary } from "../attackIntent";
import { armDirectedDeployDraft, type ActionDraft } from "../combatInspector";
import { actionStatusBlockReason, heldLockoutBlockReason } from "../actionStatusMirror";
import { withLeadingActivation } from "../activationPreview";
import { type UnitActionView, earmarkedBadge, grantedTargetingFireable } from "./unitActions";
import { inspectorEconomy, boostBlockReason } from "./inspectorEconomy";

/**
 * **A unit's one action model**, mech, NPC or pilot — the derivation and handlers behind the token command
 * menu (`TokenCommandMenuHost.tsx`, `commandMenu.ts`) and the sheets' REACTION column and
 * prepared-hold panel ({@link MechInspectorBody}, {@link NpcInspectorBody}). It was lifted out of the
 * mech sheet in `token-command-menu` Phase 1 so the menu read the SAME derivation instead of a copy;
 * Phase 4 retired the mech sheet's QUICK / FULL grid, whose arm handlers went with it (the menu arms
 * its aims itself, `commandMenuActions.ts`). `npc-command-menu` 1.1 made it kind-aware: the core
 * catalog is whichever `enumerateActions` returns for the unit's kind, and the value badge and
 * Stabilize differ by kind (below). `pilot-command-menu` 1.1 added the on-foot pilot's verbs
 * (Reload, Mount, Jockey, Ride, the Jockey follow-ups) and the Jockeyed unit's Shake Off.
 *
 * Two halves, split by what they depend on:
 *
 * - {@link unitActionData} — pure over the encounter: the enumerated descriptors, the
 *   pre-activation economy ({@link inspectorEconomy}), the row views, and the per-row status
 *   block reason (the held-action lockout first, then Boost's dead-click reason, then the status
 *   wall). No aim, no dispatch — which is what lets `commandMenu.ts`'s pure tree and its tests read
 *   it without a React host.
 * - {@link useUnitActionModel} — that data plus every handler.
 *   Every **command**-channel handler (`boost`, `prepare`) goes through
 *   `dispatchGesture(withLeadingActivation(...))`, so a ready unit's first action carries the
 *   `activate_unit` the room would have folded; the intent-channel handlers reach the room's own
 *   fold unchanged.
 */
export type UnitActionModelArgs = {
  combatant: Combatant;
  state: EncounterState;
  actorId: string;
  /** The viewer is the GM, who folds on GM-owned units (`FoldViewer`). */
  isGm: boolean;
  canControl: boolean;
  compendium: Compendium;
  /** The merged action overlay a granted row's `resolvable` is derived against. */
  overlay: LoadedOverlay;
  /** True until {@link overlay}'s sidecar fetch has settled — see {@link CompendiumState.loadingOverlay}. */
  loadingOverlay: boolean;
};

/**
 * NPC Stabilize's fixed choice (Core p.281's NPC Stabilize: clear all heat and Exposed, reload every
 * Loading weapon; never a heal or a condition clear), so it is one click with nothing to pick.
 */
export const NPC_STABILIZE = { primary: "cool", secondary: "reload" } as const satisfies { primary: StabilizePrimary; secondary: StabilizeSecondary };

/** A Jockeying rider's three options (Core p.75). */
export type JockeyMenuChoice = RequestJockeyMenu["choice"];

/** The pure half of the model — see the module doc above {@link UnitActionModelArgs}. */
export function unitActionData(args: UnitActionModelArgs) {
  const { combatant: c, state, actorId, isGm, canControl, compendium, overlay, loadingOverlay } = args;

  // Read-only mirror of the authoritative TurnBudget, folded into the action-grid
  // column headers + row dimming — identical economy model to the NPC
  // surface (a Quick costs 1 AP, a Full costs both; reactions read the GLOBAL slot, reopened at
  // every turn start).
  //
  // **Live on this mech's own turn OR pre-activation** (2.2 / D4): the room folds a real
  // `activate_unit` in front of a controlled, `"ready"` unit's first action and runs the whole gate
  // ladder against `previewActivation`'s hypothetical post-activation state, so the bar reads the
  // SAME derivation via {@link inspectorEconomy} rather than the raw `turn`. Never loosens a
  // legality function; it composes the untouched ones against the preview. `isActiveActor` below is
  // still the literal "is this mech mid-turn right now" — the correct read for END TURN, the
  // Advisor and autoplay, none of which are about what an action *would* open.
  const turn = state.activation.turn;
  const economy = inspectorEconomy({ state, combatant: c, actorId, isGm });
  const { isActiveActor, reactOpen, reactAvail } = economy;
  const ap = economy.ap;
  const reactionsLeft = economy.reactionsLeft;
  // Overcharge (RAW p.61/p.73) is a special Free action, not a Quick spend — it's legal even at 0 AP.
  //  fixed its row inheriting the QUICK column's dimming (wrong — it read "out
  // of Quick actions" as "can't Overcharge") with a bespoke `overchargeReady` flag; that flag is now
  // retired in favor of the generic once-per-turn `usedUp` mechanism below (`once-per-turn-action-
  // enforcement`), which is already orthogonal to `quickOpen`/`ap` — same fix, one
  // mechanism instead of two.


  // The PC action catalog, read off the **one** per-combatant enumeration seam:
  // `enumerateActions` returns core ({@link MECH_ACTIONS}) ∪ granted as a
  // single `ActionDescriptor[]`, and the bar splits that one list into views by `source` — core below,
  // granted just after — instead of reading `MECH_ACTIONS` for core and folding granted in separately.
  // This is a behavior-preserving representation swap: the descriptors carry the same fields the rows
  // render (name / cost / category / blurb / `resolvable`→`live`), plus the core `value` badge kind (e.g.
  // Boost's `speed`, resolved here against this mech's `speed`) — the one figure the descriptor now carries
  // so the bar reads the whole row off this list. No resolver is touched; Skirmish/Boost/Barrage/etc. keep
  // binding to their existing handlers by `id` in the row components.
  const speed = c.base.speed;
  // An NPC's badges read its tier (`npcActionValueLabel`'s accuracy and improvised damage by tier);
  // a mech's and a pilot's read their own stats (a pilot's one badge is Boost's Speed; its frozen
  // HASE is GRIT three times, so no HASE badge would be honest for it). The catalog itself is
  // kind-keyed inside `enumerateActions`.
  const valueLabelOf = (value: NonNullable<ActionDescriptor["value"]>) =>
    c.kind === "npc" ? npcActionValueLabel(value, { tier: c.tier ?? 1, speed }) : mechActionValueLabel(value as MechActionValueKind, { speed });
  // `budget` is `undefined` whenever the bar is not live, and the **previewed** budget when this
  // mech is `"ready"` and controlled (2.2) — the same budget the fold will actually mint, so the
  // once-per-turn / scene-cap `usedUp` reads below are honest for a row the player can now click.
  const activeBudget: TurnBudget | undefined = economy.budget;
  // `overlay` is the merged action overlay `resolvable` derives against —
  // see the NPC body's identical comment / `compendium.ts`'s `CompendiumState` doc.
  // `roundUses` feeds `usedUp`'s round-grain half; only
  // meaningful alongside `activeBudget` (both undefined off this mech's own turn).
  const descriptors = enumerateActions({
    combatant: c,
    compendium,
    overlay,
    budget: activeBudget,
    roundUses: state.activation.actionRoundUses[c.id],
    // `state` is what lets a granted row's authored clause
    // `condition` be answered, so a row gated out right now (the Everest's Power-Up-conditioned free
    // Boost) greys instead of offering a button whose click resolves to zero steps. Omitting it
    // degrades open — every row would read available.
    //
    // The **economy** state, not the raw one (2.2/D4): the room answers those same clauses against
    // `previewActivation`'s state (`encounterRoomCore.ts`'s `resolutionState` feeds every referee
    // call), so reading them against anything else would let the bar and the server disagree about
    // a conditioned row on a ready unit. Identical to `state` for every non-previewing case.
    state: economy.state,
  });
  // Engage COMP/CON (P4) reachability + already-Engaged mirror — reads the SAME
  // predicate/status the reducer gates `checkEngageCompConLegality` on, never a re-derived tag scan:
  // `hasCompConSystem` (the reducer's own reachability predicate, exported for this exact affordance
  // mirror) says whether the row should appear at all (most mechs carry no comp/con system, unlike
  // Overcharge/Disengage which are universal PC verbs), and `hasStatus(..., COMPCON_ENGAGED_STATUS)`
  // says whether it should render disabled/annotated because the protocol already ran (a persistent
  // marker, not a per-turn ledger entry — so this is folded into `usedUp` alongside, never instead of,
  // the descriptor's own once-per-turn read). An NPC's catalog (`NPC_ACTIONS`) has no Engage
  // COMP/CON row, so the filter below never meets one there.
  const carriesCompCon = hasCompConSystem(c.base);
  const compConEngaged = hasStatus(c.statuses, COMPCON_ENGAGED_STATUS);
  // Backswing Cut follow-up affordance — the ONE
  // derivation, read here for the row's badge/override AND re-read verbatim by `SkirmishChooser`
  // (its own `liveBackswingReservations` call, never threaded through this view a second time) and
  // by `armSkirmish`'s weapon preset below. A live reservation is scoped to THIS mech's own acting
  // turn (the helper's own contract); taking the first is deliberate — practically only one is ever
  // live at a time (every qualifying trigger hit spends the Skirmish or a Full
  // action). Mech-only by design — the NPC/pilot bodies never thread this field, so
  // their Skirmish/Fight rows simply never carry it and the shared `UnitActionRow` gate below is a
  // no-op for them.
  const backswingReservation = liveBackswingReservations(c, turn)[0];
  const backswingWeaponId = backswingReservation?.weapon;
  // The wire-honest projection — a free structural
  // upcast of `state`, read here ONLY for the Boost row's opt-in riders (below).
  const projected: ProjectedEncounterState = state;
  const views: UnitActionView[] = descriptors
    .filter((d: ActionDescriptor) => d.source === "core")
    .filter((d: ActionDescriptor) => d.id !== "engage_compcon" || carriesCompCon)
    // Take Back Control exists only while COMP/CON is in control.
    .filter((d: ActionDescriptor) => d.id !== "take_back_compcon" || compConEngaged)
    .map((d: ActionDescriptor) => ({
      id: d.id,
      name: d.name,
      cost: d.cost,
      category: d.category,
      blurb: d.blurb ?? "",
      live: d.resolvable,
      usedUp: d.id === "engage_compcon" ? d.usedUp || compConEngaged : d.usedUp,
      // A held cost earmark folds into the badge — the Everest under Power Up reads "FREE — SPD 4"
      // on its one Boost row, and reverts the moment the free use is spent.
      valueLabel: earmarkedBadge(d.value ? valueLabelOf(d.value) : null, d.costEarmarked),
      costEarmarked: d.costEarmarked,
      // Boost-only opt-in riders — see the NPC body's
      // identical comment for why this is restricted to the `"boost"` row alone.
      ...(d.id === "boost" ? { optInRiders: projected.optInActionRiders?.[c.id]?.["boost"] } : {}),
    }));
  // Loadout-granted actions off the same enumerated
  // list. The ACTIVATE- and ATTACK-routed granted actions
  // surface as rows — a granted *reaction* is Category B and must NOT enter the
  // activate list. Each is displayed off the compendium (ids-only freeze), with `resolvable` derived
  // from the overlay: resolvable rows get an activate/tech-attack affordance, advisory ones render
  // Scan-like ("GM adjudicates") and never send a `request_activate`/`request_attack` (the resolver
  // throws on a missing overlay entry). A missing-pack id falls back to a graceful label (the id).
  // `route` is threaded onto the view so `fireGranted` below can tell an ordinary granted
  // activation from a tech attack apart — WITHOUT it, a `route:"attack"` row (also `targeting:
  // "single"`, the SAME shape as an ordinary granted single-target activation) would arm the plain
  // `"activate"` aim and a board click would send `request_activate`, which the room's tech-attack
  // legality wall doesn't recognize — the row would look like it fired and silently do nothing.
  //
  // While `loadingOverlay` is true, render no granted rows rather than the classification computed
  // against a stale/bare overlay — see the NPC body's identical comment for the load-timing rationale
  // (: suppress-then-settle-once, never advisory-then-flip-to-fireable).
  const grantedView = (d: ActionDescriptor): UnitActionView => ({
    id: d.id,
    name: d.name,
    cost: d.cost,
    category: d.category,
    blurb: d.blurb ?? "",
    live: d.resolvable,
    usedUp: d.usedUp,
    valueLabel: null,
    granted: true,
    resolvable: d.resolvable,
    conditionUnmet: d.conditionUnmet,
    targeting: d.targeting,
    actionId: d.id,
    route: d.route,
    ...(d.sourceName ? { sourceName: d.sourceName } : {}),
    ...(d.sites ? { sites: d.sites } : {}),
    ...(d.quickTech ? { quickTech: d.quickTech } : {}),
    ...(d.carrierId ? { carrierId: d.carrierId } : {}),
  });
  const grantedViews: UnitActionView[] = loadingOverlay
    ? []
    : descriptors.filter((d: ActionDescriptor) => d.source === "granted" && (d.route === "activate" || d.route === "attack")).map(grantedView);

  // The sheet's REACTION column; every other row is the command menu's (`commandMenu.ts`).
  const reactionActions = views.filter((a) => a.cost === "Reaction");
  // A granted reaction taken by hand (Leader 2's Issue Command, the Kraul Line's Reel In) routes
  // `"reaction"`, so it is not among `grantedViews`: it is taken off-turn, when the menu shows no
  // groups, from the mech sheet's REACTION column. An unauthored compendium reaction (no action
  // clause) has no row; an offered one reaches its owner as a prompt.
  const grantedReactions: UnitActionView[] = loadingOverlay
    ? []
    : descriptors.filter((d: ActionDescriptor) => d.source === "granted" && d.route === "reaction" && d.resolvable).map(grantedView);
  // The status wall's affordance mirror (1.6) — the third wall the grid
  // mirrors, beside the action-point budget (the column's `available` dimming) and the once-per-turn
  // ledger (`usedUp`). CALLS `checkActorStatusLegality` per row rather than re-deriving a status list,
  // so a Shut Down mech greys every row the authority would refuse and leaves BOOT UP live off the
  // wall's own pinned exception. See {@link actionStatusBlockReason} for the id namespace + why this
  // can only ever grey a row the wall itself refuses.
  // {@link boostBlockReason} rides the SAME funnel (3.1 / D7): Boost is the one live row that
  // reaches the wire as a *command* rather than an intent, so the local reducer refuses it off-turn
  // and the controller never sends it. A ready, controlled unit now leads with `activate_unit` and
  // is live; every OTHER off-turn case is still un-sendable, and stays greyed-with-a-reason rather
  // than becoming a silently dead button (`is-unavail` is opacity, not `pointer-events`).
  // D8's held-action lockout runs FIRST, ahead of both, and answers for EVERY row: while this unit
  // holds a prepared action it may not move, act or react at all, and the room's gate-ladder arm 1
  // runs before the budget/ledger/status arms. `heldLockoutBlockReason` returns the gate's own
  // sentence, so the greyed row's title matches the toast a click would raise word for word.
  const statusBlockReason = (rowId: string) =>
    heldLockoutBlockReason(state, c.id) ?? boostBlockReason(economy, rowId) ?? actionStatusBlockReason(state, c.id, rowId);

  // Grapple follow-up state — see `useUnitActionModel`'s break/release handlers for the full story.
  const grappleMarker = c.statuses.find((s) => s.name === GRAPPLED_STATUS);
  const isGrappleAttacker = !!grappleMarker && grappleMarker.source === c.id;
  const grapplePartnerId = grappleMarker ? grapplePartner(state, c.id) : undefined;
  const grapplePartnerName = grapplePartnerId ? (state.combatants[grapplePartnerId]?.name ?? grapplePartnerId) : "its partner";
  const repairsLeft = c.current.resources?.[REPAIR_CAP_RESOURCE] ?? 0;
  // What stops this unit making attacks (the rig's Unarmed), the room's own attack-gate read: the
  // registry already drops its attack rows, and the menu greys Invade, a tech attack, on it.
  const cannotAttack = cannotMakeAttacks({ state: economy.state, actorId: c.id, overlay });
  // The invade options this unit carries (its talents' and systems' `invade_option` clauses), the
  // tech cards' "Invade ›" rows. Not filtered by target: the target is picked after the row.
  const invadeOptions = gatherInvadeOptions(state, c.id, overlay);
  const held = heldAction(state.activation, c.id);

  return {
    turn,
    economy,
    isActiveActor,
    reactOpen,
    reactAvail,
    ap,
    reactionsLeft,
    canControl,
    descriptors,
    carriesCompCon,
    compConEngaged,
    backswingWeaponId,
    views,
    grantedViews,
    reactionActions,
    grantedReactions,
    statusBlockReason,
    grappleMarker,
    isGrappleAttacker,
    grapplePartnerName,
    repairsLeft,
    cannotAttack,
    invadeOptions,
    held,
    compendium,
    overlay,
  };
}

/** The pure half's shape — what `commandMenuTree` reads. */
export type UnitActionData = ReturnType<typeof unitActionData>;

/**
 * The board-wide aim a granted row arms, or `null` for a self-targeted row that fires on the click
 * (a `request_activate` with no target). One mapping for `fireGranted` and the command menu's hover
 * preview (`commandMenuActions.ts`), so the preview paints exactly what the click would arm.
 *
 * A point-blanket throw/deploy (`targeting: "cell"` — Smoke/HEX) arms `activate_cell`; the next
 * bare-cell click supplies `site`. A system-granted TECH ATTACK (`route: "attack"`) is ALSO
 * `targeting: "single"`, so it is checked first: arming the plain `"activate"` aim for it would
 * still look like it fired while silently sending `request_activate` instead of the
 * `request_attack{tech:true}` the room's tech-attack wall expects. `accuracy: 0` — there is no
 * in-panel accuracy stepper; the CLI's `--accuracy` is the scriptable equivalent. `"sites"` arms
 * the directed-deploy pick (`v.sites` is present whenever `targeting === "sites"`). A `DEPLOYED` row
 * (`v.carrierId`, Restock) arms nothing: its target is the drone that offers it.
 */
export function grantedAimFor(attackerId: string, v: UnitActionView): ActionDraft | null {
  if (!v.actionId || v.carrierId) return null;
  if (v.targeting === "cell") return { attackerId, mode: "activate_cell", actionId: v.actionId };
  if (v.route === "attack" && v.targeting === "single") return { attackerId, mode: "tech_attack", actionId: v.actionId, accuracy: 0 };
  if (v.targeting === "single") return { attackerId, mode: "activate", actionId: v.actionId };
  if (v.targeting === "sites" && v.sites) return armDirectedDeployDraft(attackerId, v.actionId, v.sites);
  return null;
}

/**
 * The full model: {@link unitActionData} plus every handler the menu's rows and the sheet call. Uses no React hook itself today; it is named as a hook because it is the
 * renderers' one entry point and may grow memoization.
 */
export function useUnitActionModel(
  args: UnitActionModelArgs & {
    encounter: UseEncounter;
    aim: ActionDraft | null;
    setAim: (a: ActionDraft | null) => void;
  },
) {
  const { combatant: c, state, actorId, encounter, aim, setAim } = args;
  const data = unitActionData(args);
  const { isActiveActor, views, grantedViews, backswingWeaponId, grappleMarker } = data;

  const endTurn = () => actorId && encounter.dispatch({ type: "end_turn", actorId, combatantId: c.id });
  // Pass a READY unit: take and immediately end its turn, each step still hard-checked by the reducer.
  const passTurn = () => {
    if (!actorId) return;
    encounter.dispatch({ type: "activate_unit", actorId, combatantId: c.id });
    encounter.dispatch({ type: "end_turn", actorId, combatantId: c.id });
  };

  // Reuse the activate path the generic inspector already uses (CombatApp's `fireAt`/`onSelfActivate`):
  // a self action one-clicks `request_activate` with no target; a single-target action arms the board-wide
  // `activate` aim, and the next token-click fires it (`aimResolvesToFire` already includes `activate`).
  const fireGranted = (v: UnitActionView) => {
    // Advisory (`resolvable: false`) rows, and any non-fireable `targeting` kind (`"advisory"` — a
    // `"sites"` carrier with a non-literal pick count — or an unhandled future kind, fail CLOSED
    // via `grantedTargetingFireable`) never reach here; `UnitActionRow` renders them as a static row
    // with no `onClick`, but guard here too rather than trust the caller alone.
    // `v.conditionUnmet` joins the same fail-closed guard the row's own `canFire` applies —
    // firing a gated-out clause is accepted by the room and resolves to zero steps, so this must
    // never be reachable even if a caller hands over a row the bar wouldn't have offered.
    if (!v.resolvable || v.conditionUnmet || !v.actionId || !grantedTargetingFireable(v.targeting)) return;
    // `v.sites` is guaranteed whenever `targeting === "sites"` (`ActionDescriptor.sites`' own
    // contract); a malformed row arms and sends nothing, as before.
    if (v.targeting === "sites" && !v.sites) return;
    const armed = grantedAimFor(c.id, v);
    if (armed) setAim(armed);
    else encounter.sendIntent(buildActivateIntent(c.id, v.actionId, v.carrierId ? { target: v.carrierId } : {}));
  };

  const isArmed = aim !== null && aim.attackerId === c.id;
  // Boost (the live MOVE Quick row): dispatch the budget-
  // topping command directly (no target). The server is the wall — it rejects unless this unit
  // is the active actor with a Quick action left; the range overlay then reflects the +Speed.
  // `choices` — see the NPC body's identical comment.
  // **Leads with a real `activate_unit` when the room would have folded one** (3.1 / D6). Boost is
  // the one live row that rides the wire as an `encounter_command` rather than an intent, so
  // `RemoteController.dispatch` refuses a locally-illegal one WITHOUT sending it — the client has
  // to emit the activation itself or the row is a silently dead button (D7). Two ORDINARY commands
  // via `dispatchGesture`; see {@link withLeadingActivation} for why collapsing them into a
  // `batch` silently skips both hook chains.
  const boost = (choices: Record<string, string>) =>
    actorId &&
    encounter.dispatchGesture(
      withLeadingActivation({
        state,
        combatant: c,
        actorId,
        isGm: encounter.isGm,
        command: { type: "boost", actorId, combatantId: c.id, ...(Object.keys(choices).length > 0 ? { choices } : {}) },
      }),
    );
  // End the open attack lock early (dispatches `end_attack`: END BARRAGE EARLY, a Skirmish lock's
  // STOP FIRING, END FULL TECH); the server verifies the actor is mid-attack and flips the lock.
  const endAttack = () => encounter.sendIntent(buildEndAttackIntent(c.id));
  // Grapple break/release — the two follow-up affordances a currently-bound
  // unit gets, contextual on its own `Grappled` marker (there is no board target — both are self-only,
  // one-click, mirroring Overcharge/Hide's shape). `grappleMarker.source === c.id` identifies THIS unit
  // as the grapple's attacker (`checkGrappleReleaseLegality`'s own doc explains why `source`, not
  // `partner`, carries that fact): the attacker sees RELEASE GRAPPLE (unconditional free
  // action, `request_grapple_release`); the defender sees BREAK GRAPPLE (contested-HULL Quick action,
  // `request_grapple_break`, resolved via `resolveContest` server-side — this mirror invents no roll,
  // it only fires the intent). The engine is the wall for both: a break can still lose (the marker
  // persists), and a release from the wrong side is rejected — either surfaces via the toast.
  const breakGrapple = () => encounter.sendIntent(buildGrappleBreakIntent(c.id));
  const releaseGrapple = () => encounter.sendIntent(buildGrappleReleaseIntent(c.id));
  // The same-Size start-of-turn contest (Core p.69): the command menu's grapple follow-up offers it.
  const contestGrapple = () => encounter.sendIntent(buildGrappleContestIntent(c.id));
  // Stabilize's commit (the command menu's Stabilize card): ONE `request_stabilize` intent, one Full
  // action, one undo. The server resolves the chosen effects and hard-rejects a Patch with no repairs
  // remaining (`checkStabilizeLegality`); the card only mirrors that.
  const fireStabilize = (primary: StabilizePrimary, secondary?: StabilizeSecondary, secondaryStatus?: string) => {
    encounter.sendIntent(buildStabilizeIntent(c.id, { primary, secondary, secondaryStatus }));
    setAim(null);
  };
  // An NPC's Stabilize is one click: its choice is fixed ({@link NPC_STABILIZE}).
  const stabilizeNpc = () => encounter.sendIntent(buildStabilizeIntent(c.id, NPC_STABILIZE));
  // Overcharge (the live SUPPORT free row): a ONE-CLICK action — no
  // chooser, no target, no player choices (self-only, the escalating heat is rolled by the authority).
  // Folded into the QUICK column (a Free action that buys an extra Quick) but NOT gated by it — its row
  // dims off the generic once-per-turn `usedUp` ledger, not the column's Quick-action count.
  // Firing dispatches a single
  // `request_overcharge` intent; the server stays the real wall — `checkOverchargeLegality` rejects
  // unless this mech is the active actor on its own turn AND hasn't already Overcharged this turn (the
  // once-per-turn `actionUses` ledger), and NPCs are barred entirely (mech-only) — the row mirror can
  // still race a concurrent update, so a refused push surfaces through the categorized rejection toast
  // regardless. The result card (bridged into COMMS.app off the resolution rider) shows the heat taken +
  // the escalation step that advanced.
  const overcharge = () => encounter.sendIntent(buildOverchargeIntent(c.id));
  // Hide (the live STEALTH Quick row, project P3): a ONE-CLICK action — no
  // chooser, no target, self-only (mirrors Overcharge's shape). Firing dispatches a single
  // `request_hide` intent; the server is the wall — `checkHideCover` hard-rejects it in the open (no
  // cover/obscurement from every hostile), and a refusal surfaces through the categorized rejection
  // toast. The GM's override mode wraps it in `gm_force` (`decisions/0209`), past cover and budget.
  const hide = () => encounter.sendIntent(buildHideIntent(c.id));
  // Disengage (the live MOVE Full row): a ONE-CLICK action —
  // no chooser, no target, self-only (mirrors Hide/Stabilize's one-click shape, minus a legality
  // gate — RAW attaches no precondition to Disengage). Firing dispatches a single
  // `request_disengage` intent; the server charges the Full action and lands a first-class
  // `Disengaged` status that suppresses the engagement mid-move stop for the rest of the turn AND
  // gates a reaction attack targeting this mech (the manual Overwatch guard,
  // `encounterRoom.ts`'s `resolveIntent`). Engaged (the adjacency status + its ranged Difficulty)
  // is NOT suppressed.
  const disengage = () => encounter.sendIntent(buildDisengageIntent(c.id));
  // Shut Down / Boot Up (the live SUPPORT reactor pair, RAW Core pp. 71-72): two ONE-CLICK actions —
  // no chooser, no target, self-only (Disengage's shape exactly). They are ASYMMETRIC in cost, so they
  // hang off DIFFERENT columns below: Shut Down is a **Quick** action (QUICK column), Boot Up a **Full**
  // one (FULL column) — mirroring both into one column would misprice them. Firing dispatches a single
  // `request_shutdown` / `request_boot_up`; the server is the wall — Shut Down carries no precondition
  // and lands the composite `Shut Down` status (all heat + Exposed cleared, every tech-caused status
  // ended, Stunned indefinitely + hard tech-immunity even from allies), while Boot Up
  // hard-rejects an actor that isn't Shut Down (`checkBootUpLegality`) and surfaces through the toast.
  // Boot Up staying REACHABLE on a shut-down (⇒ Stunned) mech is not this row's own doing: it is the
  // status wall's pinned `act_boot_up` exception, mirrored generically by `actionStatusBlockReason`.
  const shutDown = () => encounter.sendIntent(buildShutDownIntent(c.id));
  const bootUp = () => encounter.sendIntent(buildBootUpIntent(c.id));
  // Self-Destruct's commit (the command menu's Self-Destruct card, RAW Core p.72): ONE
  // `request_self_destruct` with the chosen detonation turn; the server is the sole wall.
  const fireSelfDestruct = (at: 1 | 2 | 3) => {
    encounter.sendIntent(buildSelfDestructIntent(c.id, { at }));
    setAim(null);
  };
  // Engage COMP/CON (the live SUPPORT protocol row, project P4, RAW Core p.71):
  // a ONE-CLICK action — no chooser, no target, self-only (mirrors Overcharge's shape exactly: a 0-AP
  // action folded into the QUICK column, not gated by its `available`/AP count). Firing dispatches a
  // single `request_engage_compcon` intent; the server stays the real wall —
  // `checkEngageCompConLegality` rejects a mech with no cascade-immune AI system (`tg_ai`+`tg_no_cascade`) and a mech already
  // Engaged (this row's own `carriesCompCon`/`compConEngaged` mirror above just keeps the affordance
  // legible — it re-enforces nothing). Lands the persistent, marker-only `COMP/CON Engaged` status; no
  // behavior rides the marker (R6).
  const engageCompCon = () => encounter.sendIntent(buildEngageCompConIntent(c.id));
  // Take Back Control (Core p.106): one click, one `request_take_back_compcon`; the server's
  // `checkTakeBackCompConLegality` is the wall (start of a later turn, pilot aboard).
  const takeBackCompCon = () => encounter.sendIntent(buildTakeBackCompConIntent(c.id));
  // Search's commit (the command menu's Search card, project P3): ONE `request_search` at the picked
  // Hidden target. The server is the wall for budget, Sensors, LoS and the opposed contest.
  const fireSearch = (targetId: string) => {
    encounter.sendIntent(buildSearchIntent(c.id, targetId));
    setAim(null);
  };
  // Detect Mines (Core p.106): a one-click Quick action, no target. The request names no mine, and
  // the server resolves the sweep against the full board, so the client never learns anything a
  // failed check would not tell it.
  const detectMine = () => encounter.sendIntent(buildDetectMineIntent(c.id));
  // Disarm Mine's commit (the command menu's Disarm Mine card): ONE `request_disarm_mine` at the
  // picked adjacent mine. The server is the wall for budget, adjacency and the check.
  const fireDisarmMine = (mineId: string) => {
    encounter.sendIntent(buildDisarmMineIntent(c.id, mineId));
    setAim(null);
  };
  // Prepare / Release / Drop (RAW Core pp. 71-72) — the command menu's Prepare card composes the
  // hold ({@link PrepareControls}); {@link PreparedHoldPanel} on the sheet carries the armed hold's
  // own RELEASE NOW / DROP controls.
  // The D7 dispatch, owned HERE and not by the chooser — the same split `boost` uses above, for the
  // same reason: `prepare` is a **command**, not an intent, so `RemoteController.dispatch` refuses a
  // locally-illegal one WITHOUT sending it, and a ready-but-unactivated unit has to emit the
  // `activate_unit` the room would have folded or the PREPARE button is silently dead. TWO ORDINARY
  // COMMANDS via `dispatchGesture` — see {@link withLeadingActivation} for why collapsing them into
  // a `batch` skips `activate_unit`'s start-of-turn chain AND `prepare`'s own hook and ships green.
  //
  // The aim clears only on a SENT gesture: a locally-refused Prepare (already holding, not the
  // active actor, no Quick left, mid-attack) surfaces through the rejection toast with the composer
  // still up, so the player's typed trigger survives the refusal instead of being thrown away.
  const firePrepare = (heldAction_: HeldAction) => {
    if (!actorId) return;
    const result = encounter.dispatchGesture(
      withLeadingActivation({
        state,
        combatant: c,
        actorId,
        isGm: encounter.isGm,
        command: { type: "prepare", actorId, combatantId: c.id, held: heldAction_ },
      }),
    );
    if (result.ok) setAim(null);
  };
  const releaseArmed = isArmed && aim?.mode === "release";
  const armRelease = () => setAim(releaseArmed ? null : { attackerId: c.id, mode: "release" });
  const dropHeld = () => actorId && encounter.dispatch({ type: "drop_prepare", actorId, combatantId: c.id });
  // The on-foot pilot's verbs (Core pp.70, 75). Reload fires on the click, naming the weapon the
  // menu picked (the wire's default is the FIRST Loading weapon, which the room refuses when that
  // one is loaded). Mount, Jockey and Ride commit from their chooser cards, and Dismount (ally) is
  // a cell pick the board's `onCommitCell` sends. The server is the wall for each.
  const reload = (weapon?: string) => encounter.sendIntent(buildReloadIntent(c.id, weapon));
  const fireMount = (mechId: string) => {
    encounter.sendIntent(buildMountIntent(c.id, mechId));
    setAim(null);
  };
  const fireJockey = (targetId: string) => {
    encounter.sendIntent(buildJockeyIntent(c.id, targetId));
    setAim(null);
  };
  const fireRide = (targetId: string) => {
    encounter.sendIntent(buildRideIntent(c.id, targetId));
    setAim(null);
  };
  // A Jockeying rider's Distract / Shred / Damage, and its carrier's Shake Off (Core p.75): one
  // click each, no target (the partner is read off the markers server-side).
  const jockeyMenu = (choice: JockeyMenuChoice) => encounter.sendIntent(buildJockeyMenuIntent(c.id, choice));
  const shakeOff = () => encounter.sendIntent(buildShakeOffIntent(c.id));

  return {
    ...data,
    endTurn,
    passTurn,
    fireGranted,
    isArmed,
    boost,
    endAttack,
    breakGrapple,
    releaseGrapple,
    contestGrapple,
    fireStabilize,
    stabilizeNpc,
    overcharge,
    hide,
    disengage,
    shutDown,
    bootUp,
    fireSelfDestruct,
    engageCompCon,
    takeBackCompCon,
    fireSearch,
    detectMine,
    fireDisarmMine,
    firePrepare,
    releaseArmed,
    armRelease,
    dropHeld,
    reload,
    fireMount,
    fireJockey,
    fireRide,
    jockeyMenu,
    shakeOff,
  };
}

/** The full model's shape — the command menu and the sheet both consume it. */
export type UnitActionModel = ReturnType<typeof useUnitActionModel>;
